import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
	Alert,
	Backdrop,
	Card,
	CardContent,
	CircularProgress,
	Grid,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Chip,
	Box,
	Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { pageSizeOptions } from 'constants/constants';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { RootState } from 'store';
import { isOperatorOrHigher } from 'features/auth/helpers';

import { getPrechargedServices, getCities } from '../../services';
import { IPrechargedService, ICity, IPrechargedServiceFilters } from '../../types';
import { PrechargedServicesFilters } from '../PrechargedServicesFilters';
import routes from 'constants/routes';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	tableHeader: {
		backgroundColor: theme.palette.primary.main,
		color: 'white',
		fontWeight: 'bold',
	},
	paginationHolder: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		justifyContent: 'center',
		alignItems: 'center',
		display: 'flex',
		gap: theme.spacing(2),
	},
	card: {
		borderRadius: theme.spacing(2),
		boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
	},
	cardTitle: {
		fontSize: '1.5rem',
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		marginBottom: theme.spacing(2),
	},
	tableContainer: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
	},
	destinationChip: {
		margin: theme.spacing(0.25),
		fontSize: '0.75rem',
	},
	statusChip: {
		fontWeight: 'bold',
	},
	observationsCell: {
		maxWidth: 200,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	clickableRow: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
}));

const defaultAlertState: ShowAlertState = {
	show: false,
	severity: 'error',
	message: '',
};

export const PrechargedServicesList = (): JSX.Element => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const { auth } = useSelector((state: RootState) => state);

	const [services, setServices] = useState<IPrechargedService[]>([]);
	const [cities, setCities] = useState<ICity[]>([]);
	const [filters, setFilters] = useState<IPrechargedServiceFilters>({ availableInQuotation: true });
	const [loading, setLoading] = useState<boolean>(false);
	const [loadingCities, setLoadingCities] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [currentSize, setCurrentSize] = useState<number>(15);
	const [pageTotal, setPageTotal] = useState<number>(1);
	const [total, setTotal] = useState<number>(0);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [isStateRestored, setIsStateRestored] = useState<boolean>(false);

	// Restaurar estado desde la navegación
	useEffect(() => {
		const state = location.state as { filters?: IPrechargedServiceFilters; page?: number } | undefined;

		// Siempre aplicar filtros por defecto, incluso si hay estado previo
		const defaultFilters: IPrechargedServiceFilters = { availableInQuotation: true };

		if (state?.filters) {
			// Mantener los filtros previos pero asegurar que availableInQuotation tenga un valor por defecto
			const restoredFilters = {
				...defaultFilters,
				...state.filters,
				// Si availableInQuotation no está definido en el estado previo, usar el valor por defecto
				availableInQuotation:
					state.filters.availableInQuotation !== undefined ? state.filters.availableInQuotation : true,
			};
			setFilters(restoredFilters);
		} else {
			// Si no hay filtros en el estado, aplicar filtros por defecto
			setFilters(defaultFilters);
		}

		if (state?.page) {
			setCurrentPage(state.page);
		}
		setIsStateRestored(true);
	}, []);

	const fetchServices = useCallback(async () => {
		setLoading(true);
		try {
			const response = await getPrechargedServices(currentPage - 1, currentSize, filters);
			setServices(response.data);
			setPageTotal(response.pageTotal);
			setTotal(response.total);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error al cargar servicios precargados'),
			});
		} finally {
			setLoading(false);
		}
	}, [currentPage, currentSize, filters]);

	const fetchCities = useCallback(async () => {
		setLoadingCities(true);
		try {
			const response = await getCities();
			setCities(response.data);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'warning',
				message: `Error al cargar ciudades: ${extractErrorMessage(error, 'Error desconocido')}`,
			});
		} finally {
			setLoadingCities(false);
		}
	}, []);

	useEffect(() => {
		fetchCities();
	}, [fetchCities]);

	// Ejecutar fetchServices solo después de que el estado se haya restaurado
	useEffect(() => {
		if (isStateRestored) {
			fetchServices();
		}
	}, [fetchServices, isStateRestored]);

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setCurrentPage(value);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(Number(event.target.value));
		setCurrentPage(1);
	};

	const handleFiltersChange = (newFilters: IPrechargedServiceFilters) => {
		setFilters(newFilters);
	};

	const handleApplyFilters = () => {
		setCurrentPage(1); // Reset to first page when applying filters
		fetchServices();
	};

	const handleCloseAlert = () => {
		setAlert(defaultAlertState);
	};

	const handleRowClick = (serviceId: number) => {
		// Guardar el estado actual para poder restaurarlo al volver
		const currentState = {
			filters,
			page: currentPage,
		};
		history.push(routes.prechargedServiceDetail.replace(':id', serviceId.toString()), currentState);
	};

	const handleCreateNewService = () => {
		// Guardar el estado actual para poder restaurarlo al volver
		const currentState = {
			filters,
			page: currentPage,
		};
		history.push('/precharged-services/new', currentState);
	};

	const formatDateTime = (dateTime: string): string => {
		try {
			return format(new Date(dateTime), 'dd/MM/yyyy HH:mm', { locale: es });
		} catch {
			return dateTime;
		}
	};

	const formatDestinations = (destinations: string[]): JSX.Element => {
		if (!destinations || destinations.length === 0) {
			return (
				<Typography variant="body2" color="textSecondary">
					Sin destinos
				</Typography>
			);
		}

		return (
			<div>
				{destinations.map((destination, index) => (
					<Chip key={index} label={destination} size="small" variant="outlined" className={classes.destinationChip} />
				))}
			</div>
		);
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading || loadingCities}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<PrechargedServicesFilters
				cities={cities}
				filters={filters}
				onFiltersChange={handleFiltersChange}
				onApplyFilters={handleApplyFilters}
				loading={loading}
			/>

			<Card className={classes.card}>
				<CardContent>
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
						<Typography variant="h4" className={classes.cardTitle}>
							Servicios precargados
							{loadingCities && <Chip label="Cargando ciudades..." size="small" color="primary" sx={{ ml: 2 }} />}
						</Typography>
						{isOperatorOrHigher(auth.user) && (
							<Button variant="contained" startIcon={<Add />} onClick={handleCreateNewService} sx={{ ml: 2 }}>
								Crear nuevo servicio
							</Button>
						)}
					</Box>

					<TableContainer className={classes.tableContainer}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell className={classes.tableHeader}>ID</TableCell>
									<TableCell className={classes.tableHeader}>Nombre del servicio</TableCell>
									<TableCell className={classes.tableHeader}>Tipo</TableCell>
									<TableCell className={classes.tableHeader}>Destinos</TableCell>
									<TableCell className={classes.tableHeader}>Observaciones</TableCell>
									<TableCell className={classes.tableHeader}>Última actualización</TableCell>
									<TableCell className={classes.tableHeader}>Estado</TableCell>
									<TableCell className={classes.tableHeader}>¿Para todos los destinos?</TableCell>
									<TableCell className={classes.tableHeader}>Disponible en cotizador</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!services.length ? (
									<TableRow>
										<TableCell colSpan={9}>
											<PageNoResult />
										</TableCell>
									</TableRow>
								) : (
									services.map((service) => (
										<TableRow
											key={service.id}
											hover
											className={classes.clickableRow}
											onClick={() => handleRowClick(service.id)}
										>
											<TableCell>{service.id}</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{service.name}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">{service.serviceTypeName}</Typography>
											</TableCell>
											<TableCell>{formatDestinations(service.destinations || [])}</TableCell>
											<TableCell className={classes.observationsCell}>
												<Typography variant="body2" color="textSecondary">
													{service.observations || 'Sin observaciones'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">{formatDateTime(service.lastUpdate)}</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={service.enable ? 'Activo' : 'Inactivo'}
													color={service.enable ? 'success' : 'error'}
													size="small"
													className={classes.statusChip}
												/>
											</TableCell>
											<TableCell>
												<Chip
													label={service.forAllDestinations ? 'Sí' : 'No'}
													color={service.forAllDestinations ? 'primary' : 'default'}
													size="small"
													variant="outlined"
												/>
											</TableCell>
											<TableCell>
												<Chip
													label={service.availableInQuotation ? 'Sí' : 'No'}
													color={service.availableInQuotation ? 'success' : 'warning'}
													size="small"
													variant="filled"
												/>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<div className={classes.paginationHolder}>
						<Grid container alignItems="center" justifyContent="center" spacing={2}>
							<Grid item>
								<Typography variant="body2" color="textSecondary">
									Elementos por página:
								</Typography>
							</Grid>
							<Grid item>
								<Select value={currentSize} onChange={handleRowsPerPageChange} size="small">
									{pageSizeOptions.map((value) => (
										<MenuItem key={value} value={value}>
											{value}
										</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item>
								<Typography variant="body2" color="textSecondary">
									Total: {total} elementos
								</Typography>
							</Grid>
							<Grid item>
								<Pagination
									count={pageTotal}
									page={currentPage}
									onChange={handlePageChange}
									color="primary"
									size="medium"
								/>
							</Grid>
						</Grid>
					</div>
				</CardContent>
			</Card>

			<Snackbar open={alert.show} autoHideDuration={6000} onClose={handleCloseAlert}>
				<Alert severity={alert.severity} variant="filled">
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
